import React, { useCallback, useEffect, useState } from "react";
import { EDPadButtonMode } from "./EDPadButtonMode";
import "./DPad.css";
import { E2DPadMode } from "./E2DPadMode";
import ButtonPressHandler from "./ButtonPressHandler";
import { KeyboardKeyHandler } from "./KeyboardKeyHandler";

interface ButtonProps {
  style?: React.CSSProperties;
  buttonMode: EDPadButtonMode;
  angle: number | string;
  buttonPressHandler: ButtonPressHandler;
  keyboardKeyHandler?: KeyboardKeyHandler;
}

const MAX_KEY_DELAY = 100;

function DpadButton({
  style = {},
  buttonMode,
  angle,
  buttonPressHandler,
  keyboardKeyHandler,
}: ButtonProps) {
  const [pressed, setPressed] = useState({} as Record<string, boolean>);
  const [releaseTime, setReleaseTime] = useState({} as Record<string, number>);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      let time = new Date().getTime();
      if (releaseTime[event.keyCode] && 
        time < releaseTime[event.keyCode] + MAX_KEY_DELAY) {
        console.log("keydown event is fired after keyup...");
        return false;
      }

      if (event.repeat)
        return false;

      let newPressed = { ...pressed };
      newPressed[event.keyCode] = true;
      setPressed(newPressed);

      keyboardKeyHandler?.onKeyDown(event);
    },
    [keyboardKeyHandler]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      let newPressed = { ...pressed };
      newPressed[event.keyCode] = false;
      setPressed(newPressed);

      let newReleaseTime = { ...releaseTime };
      newReleaseTime[event.keyCode] = new Date().getTime();
      setReleaseTime(newReleaseTime);

      keyboardKeyHandler?.onKeyUp(event)
    },
    [keyboardKeyHandler]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown, false);
    document.addEventListener("keyup", onKeyUp, false);

    return () => {
      document.removeEventListener("keydown", onKeyDown, false);
      document.removeEventListener("keyup", onKeyUp, false);
    };
  }, [
    keyboardKeyHandler,
    onKeyUp,
    onKeyDown,
  ]);

  switch (buttonMode) {
    default:
    case EDPadButtonMode.Directional:
      return (
        <button
          className="dpad-button"
          style={{ ...style }}
          onMouseDown={buttonPressHandler.onButtonPress}
          onMouseUp={buttonPressHandler.onButtonRelease}
        >
          <div style={{ margin: "auto" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              transform={`rotate(${angle})`}
            >
              <path d="M24 21L16 11L8 21H13H14H18H19H24Z" fill="white" />
            </svg>
          </div>
        </button>
      );
    case EDPadButtonMode.Rotational:
      return (
        <button
          className="dpad-button"
          style={{ ...style }}
          onMouseDown={buttonPressHandler.onButtonPress}
          onMouseUp={buttonPressHandler.onButtonRelease}
        >
          <div style={{ margin: "auto" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="20"
              viewBox="0 0 24 20"
              fill="none"
              transform={`scale(${angle})`}
            >
              <path
                d="M0.802734 10.1361L6.13607 15.4694L11.4694 10.1361L9.4694 8.13607L7.4694 10.1361C7.4694 9.43926 7.5763 8.76743 7.7746 8.13607C8.62411 5.43133 11.151 3.4694 14.1361 3.4694C15.4948 3.4694 16.7586 3.87589 17.8125 4.5739L19.7256 2.66086C18.1675 1.49393 16.2325 0.802734 14.1361 0.802734C9.66791 0.802734 5.93308 3.94249 5.01752 8.13607C4.87685 8.78038 4.80274 9.44957 4.80274 10.1361L2.80273 8.13607L0.802734 10.1361Z"
                fill="#FEFEFE"
              />
              <path
                d="M16.5517 19.1514C17.2629 18.9608 17.6851 18.2297 17.4945 17.5184C17.3039 16.8071 16.5728 16.385 15.8615 16.5756C15.1502 16.7662 14.7281 17.4973 14.9187 18.2086C15.1092 18.9198 15.8404 19.342 16.5517 19.1514Z"
                fill="#FEFEFE"
              />
              <path
                d="M21.7309 12.9814C22.3686 13.3496 22.5871 14.165 22.2189 14.8027C21.8507 15.4405 21.0353 15.659 20.3975 15.2908C19.7598 14.9226 19.5413 14.1071 19.9095 13.4694C20.2777 12.8317 21.0931 12.6132 21.7309 12.9814Z"
                fill="#FEFEFE"
              />
              <path
                d="M23.1513 7.72042C22.9607 7.00913 22.2296 6.58702 21.5183 6.77761C20.807 6.9682 20.3849 7.69932 20.5755 8.41061C20.7661 9.12189 21.4972 9.544 22.2085 9.35341C22.9198 9.16283 23.3419 8.43171 23.1513 7.72042Z"
                fill="#FEFEFE"
              />
            </svg>
          </div>
        </button>
      );
  }
}

/**
 * 2-Axis directional pad
 */
interface FourDirectionalPadProps {
  verticalAxisLabel: string;
  horizontalAxisLabel: string;
  verticalAxisButtonMode: EDPadButtonMode;
  horizontalAxisButtonMode: EDPadButtonMode;
  buttonUpPressHandler: ButtonPressHandler;
  buttonDownPressHandler: ButtonPressHandler;
  buttonRightPressHandler: ButtonPressHandler;
  buttonLeftPressHandler: ButtonPressHandler;
  keyboardShortcutUp?: KeyboardKeyHandler;
  keyboardShortcutDown?: KeyboardKeyHandler;
  keyboardShortcutRight?: KeyboardKeyHandler;
  keyboardShortcutLeft?: KeyboardKeyHandler;
}
export function FourDirectionalPad({
  verticalAxisLabel,
  horizontalAxisLabel,
  verticalAxisButtonMode,
  horizontalAxisButtonMode,
  buttonUpPressHandler,
  buttonDownPressHandler,
  buttonRightPressHandler,
  buttonLeftPressHandler,
  keyboardShortcutUp,
  keyboardShortcutDown,
  keyboardShortcutRight,
  keyboardShortcutLeft
}: FourDirectionalPadProps) {
  return (
    <div className="four-directional-buttons">
      <span style={{ gridArea: "vertical-label" }}>{verticalAxisLabel}</span>

      <span style={{ gridArea: "horizontal-label" }}>
        {horizontalAxisLabel}
      </span>

      <DpadButton
        style={{ gridArea: "button-up" }}
        buttonMode={verticalAxisButtonMode}
        angle={
          verticalAxisButtonMode === EDPadButtonMode.Rotational ? "-1, 1" : 0
        }
        buttonPressHandler={buttonUpPressHandler}
        keyboardKeyHandler={keyboardShortcutUp}
      />

      <DpadButton
        style={{ gridArea: "button-right" }}
        buttonMode={horizontalAxisButtonMode}
        angle={
          horizontalAxisButtonMode === EDPadButtonMode.Rotational ? "-1, 1" : 90
        }
        buttonPressHandler={buttonRightPressHandler}
        keyboardKeyHandler={keyboardShortcutRight}
      />

      <DpadButton
        style={{ gridArea: "button-down" }}
        buttonMode={verticalAxisButtonMode}
        angle={
          verticalAxisButtonMode === EDPadButtonMode.Rotational ? "1, 1" : 180
        }
        buttonPressHandler={buttonDownPressHandler}
        keyboardKeyHandler={keyboardShortcutDown}
      />

      <DpadButton
        style={{ gridArea: "button-left" }}
        buttonMode={horizontalAxisButtonMode}
        angle={
          horizontalAxisButtonMode === EDPadButtonMode.Rotational ? "1, 1" : 270
        }
        buttonPressHandler={buttonLeftPressHandler}
        keyboardKeyHandler={keyboardShortcutLeft}
      />
    </div>
  );
}

interface TwoDirectionalPadProps {
  label: string;
  layout: E2DPadMode;
  buttonMode: EDPadButtonMode;
  positiveDirButtonPressHandler: ButtonPressHandler;
  negativeDirButtonPressHandler: ButtonPressHandler;
  negativeDirKeyboardShortcut?: KeyboardKeyHandler;
  positiveDirKeyboardShortcut?: KeyboardKeyHandler;
}

export function TwoDirectionalPad({
  label,
  layout,
  buttonMode,
  positiveDirButtonPressHandler,
  negativeDirButtonPressHandler,
  negativeDirKeyboardShortcut,
  positiveDirKeyboardShortcut
}: TwoDirectionalPadProps) {
  switch (layout) {
    case E2DPadMode.Vertical:
      return (
        <div className="two-directional-buttons-vertical">
          <span
            style={{ gridArea: "vertical-label" }}
            hidden={layout != E2DPadMode.Vertical}
          >
            {label}
          </span>

          <DpadButton
            style={{ gridArea: "button-up" }}
            buttonMode={buttonMode}
            angle={buttonMode === EDPadButtonMode.Rotational ? "-1, 1" : 0}
            buttonPressHandler={positiveDirButtonPressHandler}
            keyboardKeyHandler={positiveDirKeyboardShortcut}
          />

          <DpadButton
            style={{ gridArea: "button-down" }}
            buttonMode={buttonMode}
            angle={buttonMode === EDPadButtonMode.Rotational ? "1, 1" : 180}
            buttonPressHandler={negativeDirButtonPressHandler}
            keyboardKeyHandler={negativeDirKeyboardShortcut}
          />
        </div>
      );
    case E2DPadMode.Horizontal:
      return (
        <div className="two-directional-buttons-horizontal">
          <span
            style={{ gridArea: "horizontal-label" }}
            hidden={layout != E2DPadMode.Horizontal}
          >
            {label}
          </span>

          <DpadButton
            style={{ gridArea: "button-right" }}
            buttonMode={buttonMode}
            angle={buttonMode === EDPadButtonMode.Rotational ? "-1, 1" : 90}
            buttonPressHandler={positiveDirButtonPressHandler}
            keyboardKeyHandler={positiveDirKeyboardShortcut}
          />

          <DpadButton
            style={{ gridArea: "button-left" }}
            buttonMode={buttonMode}
            angle={buttonMode === EDPadButtonMode.Rotational ? "1, 1" : 270}
            buttonPressHandler={negativeDirButtonPressHandler}
            keyboardKeyHandler={negativeDirKeyboardShortcut}
          />
        </div>
      );
  }
}